// ============================================================================
// PHASE TEMPLATE BUILDER PAGE
// Create and edit phase templates
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Save, Plus, X, ChevronRight, FileText, Target, CheckCircle, Clock, AlertCircle, Info
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  PhaseTemplate,
  PhaseCategory,
  TemplateScope,
  CreatePhaseTemplateInput
} from '../types';
import {
  getPhaseTemplate,
  createPhaseTemplate,
  updatePhaseTemplate
} from '../services/templateService';

export default function PhaseTemplateBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { phaseTemplateId } = useParams();
  const { user } = useAuth();
  const isEditMode = !!phaseTemplateId;

  const cloneFrom = location.state?.cloneFrom as PhaseTemplate | undefined;
  const cloneName = location.state?.name as string | undefined;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('');
  const [purpose, setPurpose] = useState('');
  const [goal, setGoal] = useState('');
  const [category, setCategory] = useState<PhaseCategory>(PhaseCategory.CUSTOM);
  const [scope, setScope] = useState<TemplateScope>(TemplateScope.USER);
  const [successCriteria, setSuccessCriteria] = useState<string[]>(['']);
  const [changeNotes, setChangeNotes] = useState('');

  // UI State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingPhase, setExistingPhase] = useState<PhaseTemplate | null>(null);

  const loadPhaseTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const phase = await getPhaseTemplate(phaseTemplateId!);
      setExistingPhase(phase);
      setName(phase.name);
      setDescription(phase.description);
      setDuration(phase.duration);
      setPurpose(phase.purpose);
      setGoal(phase.goal);
      setCategory(phase.category);
      setScope(phase.scope);
      setSuccessCriteria(phase.successCriteria.length > 0 ? phase.successCriteria : ['']);
    } catch (err: any) {
      setError(err.message || 'Failed to load phase template');
    } finally {
      setLoading(false);
    }
  }, [phaseTemplateId]);

  useEffect(() => {
    if (isEditMode && phaseTemplateId) {
      loadPhaseTemplate();
    } else if (cloneFrom) {
      setName(cloneName || `${cloneFrom.name} (Copy)`);
      setDescription(cloneFrom.description);
      setDuration(cloneFrom.duration);
      setPurpose(cloneFrom.purpose);
      setGoal(cloneFrom.goal);
      setCategory(cloneFrom.category);
      setScope(TemplateScope.USER);
      setSuccessCriteria(cloneFrom.successCriteria.length > 0 ? cloneFrom.successCriteria : ['']);
    }
  }, [phaseTemplateId, isEditMode, cloneFrom, cloneName, loadPhaseTemplate]);

  const handleAddCriterion = () => setSuccessCriteria([...successCriteria, '']);
  const handleRemoveCriterion = (index: number) => setSuccessCriteria(successCriteria.filter((_, i) => i !== index));
  const handleCriterionChange = (index: number, value: string) => {
    const updated = [...successCriteria];
    updated[index] = value;
    setSuccessCriteria(updated);
  };

  const validateForm = (): string | null => {
    if (!name.trim()) return 'Phase name is required';
    if (name.trim().length < 3) return 'Phase name must be at least 3 characters';
    if (!description.trim()) return 'Description is required';
    if (description.trim().length < 10) return 'Description must be at least 10 characters';
    if (!duration.trim()) return 'Duration is required';
    if (!purpose.trim()) return 'Purpose is required';
    if (purpose.trim().length < 10) return 'Purpose must be at least 10 characters';
    if (!goal.trim()) return 'Goal is required';
    if (goal.trim().length < 10) return 'Goal must be at least 10 characters';
    const validCriteria = successCriteria.filter(c => c.trim());
    if (validCriteria.length === 0) return 'At least one success criterion is required';
    if (isEditMode && !changeNotes.trim()) return 'Change notes are required for updates';
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }

    try {
      setSaving(true);
      setError(null);
      const validCriteria = successCriteria.filter(c => c.trim());

      if (isEditMode && phaseTemplateId) {
        await updatePhaseTemplate(phaseTemplateId, {
          name: name.trim(), description: description.trim(), duration: duration.trim(),
          purpose: purpose.trim(), goal: goal.trim(), category,
          successCriteria: validCriteria, changeNotes: changeNotes.trim()
        });
        alert('Phase template updated successfully!');
        navigate('/templates/phases');
      } else {
        const input: CreatePhaseTemplateInput = {
          name: name.trim(), description: description.trim(), duration: duration.trim(),
          purpose: purpose.trim(), goal: goal.trim(), category, scope,
          successCriteria: validCriteria, createdBy: user!.userId
        };
        await createPhaseTemplate(input);
        alert('Phase template created successfully!');
        navigate('/templates/phases');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save phase template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-text-sub">Loading phase template...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light pb-24">
      <div className="w-full max-w-[900px] mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button onClick={() => navigate('/templates')} className="hover:text-primary transition-colors">Templates</button>
          <ChevronRight className="w-4 h-4" />
          <button onClick={() => navigate('/templates/phases')} className="hover:text-primary transition-colors">Phase Library</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-main font-medium">{isEditMode ? 'Edit' : 'Create'}</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2">
            {isEditMode ? 'Edit Phase Template' : 'Create Phase Template'}
          </h1>
          <p className="text-text-sub">
            {isEditMode
              ? `Creating new version of: ${existingPhase?.name || phaseTemplateId}`
              : cloneFrom
              ? `Cloning from: ${cloneFrom.name}`
              : 'Design a reusable workshop phase'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-6">
          {/* Card 1: Basic Information */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Basic Information</h3>
                <p className="text-sm text-text-sub">Core details about this phase template.</p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Phase Name <span className="text-red-500">*</span></span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Introductions & Kickoff"
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Description <span className="text-red-500">*</span></span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of what happens in this phase"
                  rows={3}
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Duration <span className="text-red-500">*</span></span>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    placeholder="e.g., 15-20 min"
                    className="w-full pl-10 pr-4 rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                  />
                </div>
                <p className="text-xs text-text-sub">Recommended time allocation for this phase</p>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-text-main">Category <span className="text-red-500">*</span></span>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as PhaseCategory)}
                    className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                  >
                    <option value={PhaseCategory.KICKOFF}>Kickoff</option>
                    <option value={PhaseCategory.DISCOVERY}>Discovery</option>
                    <option value={PhaseCategory.IDEATION}>Ideation</option>
                    <option value={PhaseCategory.RESEARCH}>Research</option>
                    <option value={PhaseCategory.ANALYSIS}>Analysis</option>
                    <option value={PhaseCategory.WRAP_UP}>Wrap Up</option>
                    <option value={PhaseCategory.CUSTOM}>Custom</option>
                  </select>
                </label>

                {!isEditMode && (
                  <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-text-main">Scope <span className="text-red-500">*</span></span>
                    <select
                      value={scope}
                      onChange={(e) => setScope(e.target.value as TemplateScope)}
                      className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                    >
                      <option value={TemplateScope.USER}>User (Private)</option>
                      <option value={TemplateScope.ORGANIZATION}>Organization (Shared)</option>
                    </select>
                    <p className="text-xs text-text-sub">Organization scope requires approval</p>
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Card 2: Purpose & Goal */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-main">Purpose & Goal</h3>
                <p className="text-sm text-text-sub">Define what this phase achieves.</p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Purpose <span className="text-red-500">*</span></span>
                <textarea
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="Why is this phase important? What value does it provide?"
                  rows={3}
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">Goal <span className="text-red-500">*</span></span>
                <textarea
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="What specific outcome should be achieved by the end of this phase?"
                  rows={3}
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
                />
              </label>
            </div>
          </div>

          {/* Card 3: Success Criteria */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-main">Success Criteria</h3>
                  <p className="text-sm text-text-sub">How do you measure success for this phase?</p>
                </div>
              </div>
              <button
                onClick={handleAddCriterion}
                className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>

            <div className="flex flex-col gap-3">
              {successCriteria.map((criterion, index) => (
                <div key={`criterion-${index}`} className="flex gap-2 items-center">
                  <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 flex-shrink-0">
                    {index + 1}
                  </div>
                  <input
                    type="text"
                    value={criterion}
                    onChange={(e) => handleCriterionChange(index, e.target.value)}
                    placeholder={`Success criterion ${index + 1}`}
                    className="flex-1 rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                  />
                  {successCriteria.length > 1 && (
                    <button
                      onClick={() => handleRemoveCriterion(index)}
                      className="p-2.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      title="Remove criterion"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Card 4: Change Notes (Edit Mode) */}
          {isEditMode && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Info className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-main">Change Notes</h3>
                  <p className="text-sm text-text-sub">
                    This will create version {(existingPhase?.versionNumber || 0) + 1}
                  </p>
                </div>
              </div>

              <textarea
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                placeholder="Describe what changed in this version..."
                rows={3}
                className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
              />
            </div>
          )}
        </div>
      </div>

      {/* Sticky Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-slate-200 z-30">
        <div className="w-full max-w-[900px] mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/templates/phases')}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : isEditMode ? 'Update Phase' : 'Create Phase'}
          </button>
        </div>
      </div>
    </div>
  );
}
